import type { ChangeEvent, MouseEventHandler } from 'react';

import { Field, FieldDescription, FieldError } from '@/components/design-system/field';
import { Input } from '@/components/design-system/input';
import InputWithIcon from '@/components/ui/InputWithIcon';
import type { TranslationKey } from '@/i18n/i18n';
import { cn } from '@/utils/cn';
import { Controller, type Control, type FieldPath, type FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FormFieldLabel } from './FormFieldLabel';

type IconComponent = React.ComponentType<{ className?: string }>;

interface FormInputProps<TFieldValues extends FieldValues> {
  control: Control<TFieldValues>;
  name: FieldPath<TFieldValues>;
  label?: TranslationKey;
  placeholder?: TranslationKey;
  description?: TranslationKey;
  tooltip?: TranslationKey;
  disabled?: boolean;
  readOnly?: boolean;
  className?: string;
  inputClassName?: string;
  autoComplete?: 'username' | 'email' | 'new-email' | 'off';
  isRequired?: boolean;
  showOptional?: boolean;
  leftIcon?: IconComponent;
  rightIcon?: IconComponent;
  onRightIconClick?: MouseEventHandler<HTMLButtonElement> | (() => void);
  status?: 'default' | 'error' | 'success' | 'warning';
  hideErrorMessage?: boolean;
  // Optional pure map applied to the value on the way in (e.g. uppercasing). Transforming here,
  // inside the Controller's onChange, avoids the render-then-rewrite flash a post-render effect
  // would cause. Defaults to identity, so existing callers are unaffected.
  transform?: (value: string) => string;
}

export const FormInput = <TFieldValues extends FieldValues = FieldValues>({
  control,
  name,
  label,
  placeholder,
  description,
  tooltip,
  disabled,
  readOnly,
  className,
  inputClassName,
  autoComplete = 'off',
  isRequired = false,
  leftIcon,
  rightIcon,
  onRightIconClick,
  status,
  hideErrorMessage = false,
  transform
}: FormInputProps<TFieldValues>) => {
  const { t } = useTranslation();

  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => {
        const boundField = transform
          ? {
              ...field,
              onChange: (event: ChangeEvent<HTMLInputElement>) => {
                field.onChange(transform(event.target.value));
              }
            }
          : field;

        return (
          <Field data-invalid={fieldState.invalid} className={cn(className)} data-testid={`form-field-${name}`}>
            {label && (
              <FormFieldLabel
                label={label}
                tooltip={tooltip}
                isRequired={isRequired}
                fieldError={fieldState.error}
                className="font-normal"
              />
            )}
            {leftIcon || rightIcon ? (
              <InputWithIcon
                {...boundField}
                status={fieldState.error ? 'error' : (status ?? 'default')}
                autoComplete={autoComplete}
                placeholder={placeholder ? t(placeholder) : undefined}
                disabled={disabled}
                leftIcon={leftIcon}
                rightIcon={rightIcon}
                onRightIconClick={onRightIconClick}
                className="w-full"
                aria-invalid={fieldState.invalid}
                data-testid={`form-input-${name}`}
              />
            ) : (
              <Input
                {...boundField}
                status={fieldState.error ? 'error' : (status ?? 'default')}
                autoComplete={autoComplete}
                placeholder={placeholder ? t(placeholder) : undefined}
                disabled={disabled}
                readOnly={readOnly}
                aria-invalid={fieldState.invalid}
                data-testid={`form-input-${name}`}
                className={inputClassName}
              />
            )}
            {description && <FieldDescription className="text-sm text-neutral-500">{t(description)}</FieldDescription>}
            {!hideErrorMessage && <FieldError errors={fieldState.error ? [fieldState.error] : undefined} />}
          </Field>
        );
      }}
    />
  );
};
